import { makeObservable, observable, computed, action } from 'mobx';
import type { TextElement, PunctElement } from '../models/TranscriptElement';
import { Segment } from '../models/Segment';
import { BaseService } from './base/BaseService';
import { 
  ISelectionService,
  SelectionEvent,
  SelectedElement,
  SelectionObserver
} from './interfaces/ISelectionService';

/**
 * Service for managing selection state in the transcript editor
 */
export class SelectionService extends BaseService<SelectionEvent> implements ISelectionService {
  
  // Selected elements
  private _selectedElements: Map<string, SelectedElement> = new Map();
  
  private _selectedSegment: Segment | null = null;

  private _currentSelectionType: 'elements' | 'segment' | null = null;
  
  /**
   * Creates a new SelectionService
   */
  constructor() {
    super(false); // Don't use BaseService's makeObservable
    
    // Use makeObservable with no specific configuration
    makeObservable(this);
  }
  
  /**
   * Selects a single element
   * 
   * @param element Element to select
   * @param segmentId ID of the segment containing the element
   * @param paragraphId ID of the paragraph containing the element
   * @param clearExisting Whether to clear existing selections
   */
  selectElement(
    element: TextElement | PunctElement,
    segmentId: string,
    paragraphId: string,
    clearExisting: boolean = true
  ): void {
    // Clear existing selections if requested
    if (clearExisting) {
      this._selectedElements.clear();
    }

    // Clear segment if it exists
    this._selectedSegment = null;

    // Set selection type to elements
    this._currentSelectionType = 'elements';
    
    // Add the selected element
    const selectedElement: SelectedElement = {
      id: element.id,
      segmentId,
      paragraphId,
      element,
      type: element.type
    };

    // Add the element to the selected elements
    this._selectedElements.set(element.id, selectedElement);

    // Notify listeners
    this.notify(SelectionEvent.ELEMENT_SELECTED, {
      element: selectedElement,
      selectedElements: this.getSelectedElements()
    });
  }
  
  /**
   * Selects multiple elements
   * 
   * @param elements Elements to select with their context
   * @param clearExisting Whether to clear existing selections
   */
  selectElements(
    elements: Array<{
      element: TextElement | PunctElement;
      segmentId: string;
      paragraphId: string;
    }>,
    clearExisting: boolean = true
  ): void {
    // Clear segment if it exists
    this._selectedSegment = null;

    // Set selection type to elements
    this._currentSelectionType = 'elements';

    // Clear existing selections if requested
    if (clearExisting) {
      this._selectedElements.clear();
    }
    
    // Add the selected elements
    for (const { element, segmentId, paragraphId } of elements) {
      const selectedElement: SelectedElement = {
        id: element.id,
        segmentId,
        paragraphId,
        element,
        type: element.type
      };
      
      this._selectedElements.set(element.id, selectedElement);
    }
    
    // Notify listeners
    this.notify(SelectionEvent.ELEMENT_SELECTED, {
      selectedElements: this.getSelectedElements()
    });
  }
  
  /**
   * Deselects a specific element
   * 
   * @param elementId ID of the element to deselect
   */
  deselectElement(elementId: string): void {
    if (!this._selectedElements.has(elementId)) {
      return;
    }
    
    // Remove the element
    this._selectedElements.delete(elementId);
    
    // Notify listeners
    this.notify(SelectionEvent.SELECTION_CHANGED, {
      selectedElements: this.getSelectedElements()
    });
  }
  
  /**
   * Clears all selected elements
   */
  clearSelectedElements(): void {
    this._selectedElements.clear();
    
    // Notify listeners
    this.notify(SelectionEvent.SELECTION_CLEARED);
  }
  
  /**
   * Clears all selections (both text and elements)
   */
  clearAllSelections(): void {
    this._selectedElements.clear();
    this._selectedSegment = null;
    this._currentSelectionType = null;

    // Notify listeners
    this.notify(SelectionEvent.SELECTION_CLEARED);
  }
  
  /**
   * Checks if a specific element is currently selected
   * 
   * @param elementId Element ID to check
   * @returns True if the element is selected
   */
  isElementSelected(elementId: string): boolean {
    return this._selectedElements.has(elementId);
  }
  
  /**
   * Gets the currently selected elements
   * 
   * @returns Array of selected elements
   */
  getSelectedElements(): SelectedElement[] {
    return Array.from(this._selectedElements.values());
  }
  
  
  // For backward compatibility with existing code
  registerObserver(observer: SelectionObserver): void {
    this.on(SelectionEvent.SELECTION_CHANGED, (data) => observer.onSelectionEvent(SelectionEvent.SELECTION_CHANGED, data));
    this.on(SelectionEvent.ELEMENT_SELECTED, (data) => observer.onSelectionEvent(SelectionEvent.ELEMENT_SELECTED, data));
    this.on(SelectionEvent.SELECTION_CLEARED, (data) => observer.onSelectionEvent(SelectionEvent.SELECTION_CLEARED, data));
  }
  
  // For backward compatibility with existing code
  unregisterObserver(observer: SelectionObserver): void {
    // Since we can't easily find the specific wrapper functions we created during registration,
    // we'll need to create a new event system in a backward-compatible way later.
    // This implementation is just a temporary shim to prevent type errors.
  }
  
  /**
   * Registers a callback for selection updates
   * This is used to synchronize multiple SelectionService instances
   * @param callback Function to call when selection changes
   * @returns Function to unregister the callback
   */
  registerForSelectionUpdates(callback: (selectedElements: SelectedElement[]) => void): () => void {
    const handleSelectionChange = (data: any) => {
      callback(this.getSelectedElements());
    };
    
    // Register for all selection events
    this.on(SelectionEvent.SELECTION_CHANGED, handleSelectionChange);
    this.on(SelectionEvent.ELEMENT_SELECTED, handleSelectionChange);
    this.on(SelectionEvent.SELECTION_CLEARED, () => callback([]));
    
    // Return unregister function
    return () => {
      this.off(SelectionEvent.SELECTION_CHANGED, handleSelectionChange);
      this.off(SelectionEvent.ELEMENT_SELECTED, handleSelectionChange);
      this.off(SelectionEvent.SELECTION_CLEARED, () => callback([]));
    };
  }
  
  /**
   * Sets the selected elements directly
   * This is used when synchronizing from another SelectionService
   * @param elements Selected elements to set
   */
  setSelectedElements(elements: SelectedElement[]): void {
    // Clear existing selections
    this._selectedElements.clear();
    
    // Set selection type to elements if we have elements, otherwise null
    this._currentSelectionType = elements.length > 0 ? 'elements' : null;
    
    // Add the selected elements
    for (const element of elements) {
      this._selectedElements.set(element.id, element);
    }
    
    // Notify listeners
    this.notify(SelectionEvent.SELECTION_CHANGED, {
      selectedElements: this.getSelectedElements()
    });
  }

  /**
   * Sets the selected segment
   * @param segment Segment to select
   */
  setSelectedSegment(segment: Segment): void {
    this._selectedSegment = segment;
    this._currentSelectionType = 'segment';
  }
  
  /**
   * Clean up resources
   */
  override cleanup(): void {
    super.cleanup();
    this._selectedElements.clear();
    this._selectedSegment = null;
    this._currentSelectionType = null;
  }
  
  // Public getters
  get selectedElements(): SelectedElement[] { return this.getSelectedElements(); }
  get selectedSegment(): Segment | null { return this._selectedSegment; }
  get currentSelectionType(): 'elements' | 'segment' | null { return this._currentSelectionType; }
  get hasSelection(): boolean { return this._currentSelectionType !== null; }
}